import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/Pagination';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PER_PAGE = 25;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications?page=${page}&per_page=${PER_PAGE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || response.data || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const toggleSelected = (id, event) => {
    event.stopPropagation();
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/notifications/delete`, { ids: selectedIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.deleted} notification${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds([]);
      setSelectMode(false);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Could not delete those notifications');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete every notification? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/notifications/delete`, { all: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.deleted} notification${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds([]);
      setSelectMode(false);
      setPage(1);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Could not clear your notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_content': return '📚';
      case 'folder_unlocked': return '🔓';
      case 'live_stream': return '🎥';
      case 'calendar_event': return '📅';
      case 'forum_reply': return '💬';
      case 'admin_message': return '✉️';
      case 'announcements': return '📢';
      case 'vendor_approved': return '🎉';
      default: return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-white hover:text-cyan-300 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="notification-unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - Rendered as Portal */}
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed left-1/2 top-20 -translate-x-1/2 w-96 bg-white rounded-lg shadow-xl border z-[9999] max-h-[500px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            {notifications.length > 0 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-cyan-600 hover:text-cyan-700"
                  data-testid="notifications-mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectMode((v) => !v); setSelectedIds([]); }}
                  className="text-gray-600 hover:text-gray-800"
                  data-testid="notifications-select-mode-btn"
                >
                  {selectMode ? 'Cancel' : 'Select'}
                </Button>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={(e) => (selectMode
                      ? toggleSelected(notification.id, e)
                      : handleNotificationClick(notification))}
                    data-testid={`notification-item-${notification.id}`}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-cyan-50' : ''
                    } ${selectedIds.includes(notification.id) ? 'ring-2 ring-inset ring-cyan-400' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {selectMode ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(notification.id)}
                          onChange={(e) => toggleSelected(notification.id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 h-4 w-4 accent-cyan-600"
                          data-testid={`notification-checkbox-${notification.id}`}
                        />
                      ) : (
                        <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                            {notification.title}
                          </h4>
                          <button
                            onClick={(e) => deleteNotification(notification.id, e)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-400">{formatTime(notification.created_at)}</span>
                          {!notification.is_read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50">
            {notifications.length > 0 && (
              <div className="mb-2 flex items-center gap-2">
                {selectMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    disabled={selectedIds.length === 0}
                    onClick={deleteSelected}
                    data-testid="notifications-delete-selected-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete selected{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={deleteAll}
                  data-testid="notifications-delete-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete all
                </Button>
              </div>
            )}
            {totalPages > 1 && (
              <div style={{ marginBottom: 8 }}>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onChange={setPage}
                  testidPrefix="notifications-pagination"
                  theme="light"
                />
              </div>
            )}
            <Button
              variant="outline"
              className="w-full"
              data-testid="notifications-settings-btn"
              onClick={() => {
                setIsOpen(false);
                navigate('/dashboard/settings');
              }}
            >
              Notification Settings
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
